if (!customElements.get('newsletter-popup')) {
  customElements.define(
    'newsletter-popup',
    class NewsletterPopup extends HTMLElement {
      connectedCallback() {
        // Same move as Dawn's ModalDialog: escape any ancestor that would clip or offset `position: fixed`.
        // Editing settings re-renders the section, so drop the copy left behind on the body.
        if (this.parentElement !== document.body) {
          document.querySelectorAll(`newsletter-popup[data-section-id="${this.dataset.sectionId}"]`).forEach((el) => {
            if (el !== this) el.remove();
          });
          document.body.appendChild(this);
        }

        if (this.initialized) return;
        this.initialized = true;

        this.dialog = this.querySelector('[role="dialog"]');
        this.success = this.querySelector('.newsletter-form__message--success');
        this.error = this.querySelector('.newsletter-form__message:not(.newsletter-form__message--success)');
        this.designMode = Boolean(window.Shopify && window.Shopify.designMode);
        this.storageKey = this.dataset.storageKey;
        this.frequency = this.dataset.frequency;

        this.querySelectorAll('[data-newsletter-popup-close]').forEach((element) => {
          element.addEventListener('click', this.hide.bind(this));
        });
        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hide();
        });

        if (this.designMode) {
          this.watchThemeEditor();
          return;
        }

        // The form posts and reloads the page, so a result waiting in the markup means the visitor
        // just submitted: reopen so they see the outcome rather than a page that swallowed it.
        if (this.success) {
          this.remember('subscribed');
          this.show();
          return;
        }

        if (this.error) {
          this.show();
          return;
        }

        if (this.alreadyHandled()) return;
        this.timer = setTimeout(this.show.bind(this), Number(this.dataset.delay || 0) * 1000);
      }

      disconnectedCallback() {
        // Appending to the body disconnects and reconnects this element; only tear down a real removal.
        Promise.resolve().then(() => {
          if (this.isConnected) return;
          clearTimeout(this.timer);
          if (this.hasAttribute('open')) {
            this.removeAttribute('open');
            document.body.classList.remove('overflow-hidden');
            removeTrapFocus();
          }
          if (this.onSectionSelect) document.removeEventListener('shopify:section:select', this.onSectionSelect);
          if (this.onSectionDeselect) document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
          this.initialized = false;
        });
      }

      // In the theme editor the popup follows section selection so the merchant can style it.
      watchThemeEditor() {
        this.onSectionSelect = (event) => {
          if (event.detail.sectionId === this.dataset.sectionId) this.show();
        };
        this.onSectionDeselect = (event) => {
          if (event.detail.sectionId === this.dataset.sectionId) this.hide();
        };
        document.addEventListener('shopify:section:select', this.onSectionSelect);
        document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
      }

      show() {
        if (this.hasAttribute('open')) return;

        // Another overlay owns the screen (cart drawer, menu, cart reminder); wait it out.
        if (document.body.classList.contains('overflow-hidden')) {
          clearTimeout(this.timer);
          this.timer = setTimeout(this.show.bind(this), 3000);
          return;
        }

        this.openedBy = document.activeElement;
        this.setAttribute('open', '');
        document.body.classList.add('overflow-hidden');
        // Focus the confirmation when there is one so it is announced; otherwise the dialog itself,
        // rather than the email field, which would throw up the keyboard on mobile.
        trapFocus(this, this.success || this.dialog);
      }

      hide() {
        if (!this.hasAttribute('open')) return;
        this.removeAttribute('open');
        document.body.classList.remove('overflow-hidden');
        removeTrapFocus(this.openedBy);

        if (this.designMode) return;
        // A subscriber is done with this popup for good; do not downgrade that to a cooldown.
        if (this.readStorage() !== 'subscribed') this.remember(String(Date.now()));
      }

      alreadyHandled() {
        const stored = this.readStorage();
        if (!stored) return false;
        if (stored === 'subscribed') return true;
        if (this.frequency === 'never') return true;

        return Date.now() - Number(stored) < Number(this.frequency) * 24 * 60 * 60 * 1000;
      }

      remember(value) {
        try {
          localStorage.setItem(this.storageKey, value);
        } catch (e) {
          // Storage can be unavailable (private browsing, blocked cookies); the popup just shows again.
        }
      }

      readStorage() {
        try {
          return localStorage.getItem(this.storageKey);
        } catch (e) {
          return null;
        }
      }
    }
  );
}
